import { useCallback, useMemo, useState } from "react";

import { BooleanFilter } from "@/components/ui/BooleanFilters/boolean-filters.types";

type Options<TData, TKey extends string = string> = {
    filters: BooleanFilter<TData, TKey>[];
    exclusive?: boolean;
    defaultActiveFilters?: TKey[];
    includeAllOption?: boolean;
};

export const useBooleanFilters = <TData, TKey extends string = string>(options: Options<TData, TKey>) => {
    const { exclusive, defaultActiveFilters, includeAllOption } = options;
    const [activeFilters, setActiveFilters] = useState<TKey[]>(defaultActiveFilters ?? []);

    const filters = useMemo(() => {
        if (includeAllOption) {
            return [
                {
                    key: "all",
                    title: "All",
                    condition: () => true,
                },
                ...options.filters,
            ];
        } else {
            return options.filters;
        }
    }, [options.filters, includeAllOption]);

    const setActiveFilter = useCallback(
        (filter: TKey) => {
            if (exclusive === true) {
                setActiveFilters([filter]);
            } else {
                setActiveFilters((prev) => {
                    if (prev.includes(filter)) {
                        return prev.filter((f) => f !== filter);
                    } else {
                        return [...prev, filter];
                    }
                });
            }
        },
        [exclusive, setActiveFilters],
    );

    const applyFilters = useCallback(
        (data: TData[]) => {
            return data.filter((item) => {
                const activeFiltersConditions = activeFilters.map(
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore Generic type of filter key is not perfect due to the internal usage of "all" in exclusive filter
                    (filter) => filters.find((f) => f.key === filter).condition,
                );
                if (exclusive === true) {
                    // Show items that match all active filters
                    return activeFiltersConditions.every((condition) => condition(item));
                } else {
                    // If no filters are active, show all items that don't match any filter
                    if (activeFiltersConditions.length === 0) {
                        return filters.every((filter) => !filter.condition(item));
                    }

                    // Show items that match any active filter
                    return activeFiltersConditions.some((condition) => condition(item));
                }
            });
        },
        [filters, activeFilters, exclusive],
    );

    return { filters, activeFilters, setActiveFilter, applyFilters };
};
